import { EditIcon } from "@chakra-ui/icons";
import { Box, Button, Checkbox, HStack, IconButton, Image, Input, Modal, ModalBody, ModalCloseButton, ModalContent, ModalHeader, ModalOverlay, Spinner, Stack, Table, TableContainer, Tbody, Td, Text, Th, Thead, Tr, useDisclosure, useToast } from "@chakra-ui/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { adminDeleteCompanies, adminDeleteCompanyImage, adminGetCompanies, adminUpdateCompanyImage, uploadFileForCompany } from "../../../api";
import AdminOnly from "../../../components/Auth/AdminOnly";
import MyIcon from "../../../components/Icon/MyIcon";
import ImageModal from "../../../components/Modals/ImageModal";
import { colors } from "../../../styles";
import { IAdminCompany, IAdminCompanyList } from "../../../types";
import { formatDateTimeKor } from "../../../utils";
import EditBizRegNoModal from "./EditBizRegNoModal";
import EditContactModal from "./EditContactModal";
import EditHomepageModal from "./EditHomepageModal";
import EditMallModal from "./EditMallModal";
import MedicinesModal from "./MedicinesModal";

export default function Companies() {

    /* pagination */

    const [page, setPage] = useState(1);
    const size = 100;

    /* form */

    interface ISearchForm {
        keyword: string;
    }

    const { register, handleSubmit } = useForm<ISearchForm>({
        mode: "onChange",
    });

    const [keyword, setKeyword] = useState<string | null>(null);

    const onSubmit = ({ keyword }: ISearchForm) => {
        setPage(1);
        setKeyword(keyword);
    }

    /* query */

    const { data: companyListData, isLoading: isCompanyListDataLoading } = useQuery<IAdminCompanyList>({
        queryKey: ['adminGetCompanies', page, size, keyword],
        queryFn: () => adminGetCompanies({ page, size, keyword }),
        enabled: true,
    });

    const totalPages = useMemo(() => companyListData?.totalPages ?? 0, [companyListData]);
    const currentPage = useMemo(() => companyListData?.currentPage ?? 0, [companyListData]);

    /* pagination */

    const pages = useMemo(() => {
        const startPage = Math.max(page - 4, 1);
        const endPage = Math.min(page + 5, totalPages);
        return Array.from({ length: Math.max(0, endPage - startPage + 1) }, (_, idx) => startPage + idx);
    }, [page, totalPages]);

    /* company columns */

    const companyColumns = [
        { key: "name", label: "회사 이름" },
        { key: "medicineCount", label: "약품 수" },
        { key: "bizRegNo", label: "사업자 등록번호" },
        { key: "image", label: "회사 이미지" },
        { key: "contact", label: "연락처" },
        { key: "homepage", label: "홈페이지" },
        { key: "mall", label: "쇼핑몰" },
        { key: "createdAt", label: "생성 일자" },
        { key: "updatedAt", label: "수정 일자" }
    ]

    /* selection */

    const [selectedCompanyIds, setSelectedCompanyIds] = useState<string[]>([]);
    const currentPageCompanyIds = useMemo(() => companyListData?.companies.map((company) => company.id) ?? [], [companyListData]);

    const isAllCurrentPageSelected = useMemo(() => {
        return currentPageCompanyIds.length > 0 && currentPageCompanyIds.every((id) => selectedCompanyIds.includes(id));
    }, [currentPageCompanyIds, selectedCompanyIds]);

    const isSomeCurrentPageSelected = useMemo(() => {
        const someSelected = currentPageCompanyIds.some((id) => selectedCompanyIds.includes(id));
        return someSelected && !isAllCurrentPageSelected;
    }, [currentPageCompanyIds, selectedCompanyIds, isAllCurrentPageSelected]);

    const toggleSelectAllCurrentPage = () => {
        if (isAllCurrentPageSelected) {
            setSelectedCompanyIds((prev) => prev.filter((id) => !currentPageCompanyIds.includes(id)));
        } else {
            setSelectedCompanyIds((prev) => Array.from(new Set([...prev, ...currentPageCompanyIds])));
        }
    };

    const toggleSelectOne = (companyId: string) => {
        setSelectedCompanyIds((prev) => (prev.includes(companyId) ? prev.filter((id) => id !== companyId) : [...prev, companyId]));
    };

    /* delete companies modal */

    const { isOpen: isDeleteCompaniesModalOpen, onOpen: onOpenDeleteCompaniesModal, onClose: onCloseDeleteCompaniesModal } = useDisclosure();

    const onDeleteCompanies = () => {
        deleteCompaniesMutation.mutate({ companyIds: selectedCompanyIds });
        onCloseDeleteCompaniesModal();
    };

    /* company image */

    const queryClient = useQueryClient();
    const toast = useToast();

    /* mutation */

    const deleteCompaniesMutation = useMutation({
        mutationFn: adminDeleteCompanies,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['adminGetCompanies', page, size] });
            setSelectedCompanyIds([]);

            toast({
                title: "제약사 삭제에 성공했습니다.",
                status: "success",
                position: "top",
                duration: 2000,
                isClosable: true,
            });
        },
        onError: (error) => {
            console.error(error);
            toast({
                title: "제약사 삭제에 실패했습니다.",
                status: "error",
                position: "top",
                duration: 2000,
                isClosable: true,
            });
        },
    });

    const [uploadingCompanyId, setUploadingCompanyId] = useState<string | null>(null);

    /* upload company image: AWS 업로드 + DB 반영 */

    const uploadCompanyImageMutation = useMutation({
        mutationFn: async ({ companyId, file }: { companyId: string; file: File }) => {
            const imageUrl = await uploadFileForCompany({ companyId, file });
            await adminUpdateCompanyImage({ companyId, imageUrl });
            return imageUrl;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['adminGetCompanies'] });
            toast({
                title: "회사 이미지를 저장했습니다.",
                status: "success",
                position: "top",
                duration: 2000,
                isClosable: true,
            });
        },
        onError: (error) => {
            console.error(error);
            toast({
                title: "회사 이미지 수정에 실패했습니다.",
                status: "error",
                position: "top",
                duration: 2000,
                isClosable: true,
            });
        },
    });

    /* delete company image mutation */

    const deleteCompanyImageMutation = useMutation({
        mutationFn: adminDeleteCompanyImage,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['adminGetCompanies'] });
        },
        onError: (error) => {
            console.error(error);
            toast({
                title: "회사 이미지 삭제에 실패했습니다.",
                status: "error",
                position: "top",
                duration: 2000,
                isClosable: true,
            });
        },
    });

    const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>, companyId: string) => {
        try {
            const file = event.target.files?.[0];
            if (!file) return;

            setUploadingCompanyId(companyId);
            await uploadCompanyImageMutation.mutateAsync({ companyId, file });
        } finally {

            ///////////////////////////////////////////////////////////////////////// 같은 파일 재업로드 가능하도록 값 초기화

            if (event.target) event.target.value = "";
            setUploadingCompanyId(null);
        }
    };

    const handleDeleteCompanyImage = async (companyId: string) => {
        await deleteCompanyImageMutation.mutateAsync({ companyId });
    };

    /* modal */

    const { isOpen:isImageModalOpen, onOpen:onOpenImageModal, onClose:onCloseImageModal } = useDisclosure();

    const [selectedImageUrl, setSelectedImageUrl] = useState<string | null>(null);

    const openImage = (url: string) => {
        setSelectedImageUrl(url);
        onOpenImageModal();
    };

    const closeImage = () => {
        setSelectedImageUrl(null);
        onCloseImageModal();
    };

    /* edit company field modals */

    const [selectedCompany, setSelectedCompany] = useState<IAdminCompany | null>(null);

    const { isOpen: isBizRegNoModalOpen, onOpen: onOpenBizRegNoModal, onClose: onCloseBizRegNoModal } = useDisclosure();
    const { isOpen: isContactModalOpen, onOpen: onOpenContactModal, onClose: onCloseContactModal } = useDisclosure();
    const { isOpen: isHomepageModalOpen, onOpen: onOpenHomepageModal, onClose: onCloseHomepageModal } = useDisclosure();
    const { isOpen: isMallModalOpen, onOpen: onOpenMallModal, onClose: onCloseMallModal } = useDisclosure();

    const openBizRegNoModal = (company: IAdminCompany) => {
        setSelectedCompany(company);
        onOpenBizRegNoModal();
    };

    const closeBizRegNoModal = () => {
        setSelectedCompany(null);
        onCloseBizRegNoModal();
    };

    const openContactModal = (company: IAdminCompany) => {
        setSelectedCompany(company);
        onOpenContactModal();
    };

    const closeContactModal = () => {
        setSelectedCompany(null);
        onCloseContactModal();
    };

    const openHomepageModal = (company: IAdminCompany) => {
        setSelectedCompany(company);
        onOpenHomepageModal();
    };

    const closeHomepageModal = () => {
        setSelectedCompany(null);
        onCloseHomepageModal();
    };

    const openMallModal = (company: IAdminCompany) => {
        setSelectedCompany(company);
        onOpenMallModal();
    };

    const closeMallModal = () => {
        setSelectedCompany(null);
        onCloseMallModal();
    };

    /* medicines modal */

    const [medicinesCompany, setMedicinesCompany] = useState<IAdminCompany | null>(null);
    const { isOpen: isMedicinesModalOpen, onOpen: onOpenMedicinesModal, onClose: onCloseMedicinesModal } = useDisclosure();

    const openMedicinesModal = (company: IAdminCompany) => {
        setMedicinesCompany(company);
        onOpenMedicinesModal();
    };

    const closeMedicinesModal = () => {
        onCloseMedicinesModal();
        setMedicinesCompany(null);
    };

    const editableFieldComponent = (
        value: string | undefined,
        onEdit: () => void,
    ) => {
        return (
            <HStack spacing={2} justifyContent={"flex-start"} alignItems={"center"}>
                <Text fontSize={14} color={colors.black} noOfLines={1} maxWidth={"180px"}>
                    {value || "-"}
                </Text>
                <IconButton
                    aria-label="edit"
                    icon={<EditIcon />}
                    size="xs"
                    variant="ghost"
                    onClick={onEdit}
                />
            </HStack>
        );
    };

    /* image component */

    const imageComponent = (companyId: string, image: string | undefined) => {

        const isUploading = (uploadingCompanyId === companyId) && uploadCompanyImageMutation.isPending;
        const inputId = `${companyId}-image-upload`;

        return (
            <HStack width={"100%"} justifyContent={"center"} alignItems={"center"} spacing={2}>
                <input
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    id={inputId}
                    onChange={(event) => handleFileChange(event, companyId)}
                />

                {image ? (
                    <Box
                        width={8}
                        height={8}
                        borderRadius={2}
                        border={"1px solid " + colors.g5}
                        backgroundColor={colors.g5}
                        onClick={() => openImage(image)}
                        cursor={"pointer"}
                    >
                        <Image src={image} width={"100%"} height={"100%"} objectFit={"cover"} />
                    </Box>
                ) : null}

                {!image &&
                    <Box
                        as="button"
                        width={8}
                        height={8}
                        onClick={() => document.getElementById(inputId)?.click()}
                        cursor={"pointer"}
                        display={"flex"}
                        justifyContent={"center"}
                        alignItems={"center"}
                        disabled={isUploading}
                    >
                        {isUploading ? <Spinner size="sm" /> : <MyIcon src={"/svg/photo.svg"} alt="photo" width={32} height={32} />}
                    </Box>
                }

                {image ? (
                    <Box onClick={() => handleDeleteCompanyImage(companyId)} cursor={"pointer"}>
                        <MyIcon src={"/svg/delete-circle.svg"} alt="delete" width={32} height={32} />
                    </Box>
                ) : null}
            </HStack>
        );
    }

    /* render */

    return (
        <AdminOnly>
            <Stack width={"100%"} padding={10}>
                <HStack justifyContent={"space-between"} alignItems={"center"} gap={10}>
                    <HStack justifyContent={"flex-start"} alignItems={"center"} gap={3}>
                        <Text fontSize={28} fontWeight={700} color={colors.black}>제약사</Text>
                        <Text fontSize={28} fontWeight={400} color={colors.gray}>{companyListData?.totalCount}</Text>
                    </HStack>
                    <HStack gap={3}>
                    </HStack>
                </HStack>
                <HStack as="form" onSubmit={handleSubmit(onSubmit)} marginY={10}>
                    <Input 
                        {...register("keyword", {required:false})}
                        type="text" 
                        placeholder="Search" 
                        width={"50%"} 
                    />
                    <Box as="button" type="submit">
                        <MyIcon src={"/svg/search.svg"} alt="search" width={24} height={24} />
                    </Box>
                </HStack>

                {/* trash button */}

                <HStack justifyContent={"flex-start"} marginY={0}>
                    <Button 
                        size={"sm"} 
                        variant={"solid"} 
                        isDisabled={selectedCompanyIds.length === 0} 
                        opacity={selectedCompanyIds.length === 0 ? 0.1 : 1}
                        onClick={onOpenDeleteCompaniesModal}
                    >
                        <MyIcon src={"/svg/trash-filled.svg"} alt="trash" width={16} height={16} />
                    </Button>
                    <Modal isOpen={isDeleteCompaniesModalOpen} onClose={onCloseDeleteCompaniesModal}>
                        <ModalOverlay />
                        <ModalContent>
                            <ModalHeader>
                                <Text fontSize={20} fontWeight={700} color={colors.black}>삭제하기</Text>
                                <ModalCloseButton />
                            </ModalHeader>
                            <ModalBody>
                                <Text fontSize={16} fontWeight={400} color={colors.black}>선택된 행 수: {selectedCompanyIds.length}</Text>
                                <Text fontSize={16} fontWeight={400} color={colors.black}>정말 삭제하시겠습니까?</Text>
                            </ModalBody>
                            <Stack marginTop={5} width={"100%"} padding={3}>
                                <Button
                                    onClick={onDeleteCompanies}
                                    isLoading={deleteCompaniesMutation.isPending}
                                    width={"100%"}
                                    backgroundColor={colors.blue}
                                    color={colors.white}
                                    borderRadius={10}
                                    _hover={{ bg: colors.blue }}
                                    boxShadow="sm"
                                >
                                    삭제
                                </Button>
                            </Stack>
                        </ModalContent>
                    </Modal>
                </HStack>

                {/* table */}

                {!isCompanyListDataLoading ? (
                    <TableContainer marginTop={5}>
                        <Table variant='simple' scrollBehavior="smooth">
                            <Thead backgroundColor={colors.g5}>
                                <Tr>
                                    <Th>
                                        <Checkbox 
                                            isChecked={isAllCurrentPageSelected} 
                                            isIndeterminate={isSomeCurrentPageSelected} 
                                            onChange={toggleSelectAllCurrentPage}
                                        />
                                    </Th>
                                    {companyColumns.map((column) => (  
                                        <Th key={column.key}>{column.label}</Th>
                                    ))}
                                </Tr>
                            </Thead>
                            <Tbody>
                                {companyListData?.companies.map((company) => (
                                    <Tr key={company.id}>
                                        <Td>
                                            <Checkbox 
                                                isChecked={selectedCompanyIds.includes(company.id)}
                                                onChange={() => toggleSelectOne(company.id)}
                                            />
                                        </Td>
                                        {companyColumns.map((column, index) => (
                                            <Td key={index}>
                                                {column.key === "image" ? imageComponent(company.id, company.image)
                                                    : column.key === "name"
                                                        ? (
                                                            <Button
                                                                size={"md"}
                                                                variant={"outline"}
                                                                borderColor={colors.blue}
                                                                onClick={() => openMedicinesModal(company)}
                                                            >
                                                                {company.name ?? "-"}
                                                            </Button>
                                                        )
                                                        : column.key === "bizRegNo"
                                                            ? editableFieldComponent(company.bizRegNo, () => openBizRegNoModal(company))
                                                            : column.key === "contact"
                                                                ? editableFieldComponent(company.contact, () => openContactModal(company))
                                                                : column.key === "homepage"
                                                                    ? editableFieldComponent(company.homepage, () => openHomepageModal(company))
                                                                    : column.key === "mall"
                                                                        ? editableFieldComponent(company.mall, () => openMallModal(company))
                                                                        : (column.key === "createdAt" || column.key === "updatedAt")
                                                                            ? formatDateTimeKor(company[column.key as keyof IAdminCompany] as string)
                                                                            : String(company[column.key as keyof IAdminCompany] ?? "-")}
                                            </Td>
                                        ))}
                                    </Tr>
                                ))}
                            </Tbody>
                        </Table>

                        {/* image Modal */}
            
                        {selectedImageUrl && 
                            <ImageModal 
                                isOpen={isImageModalOpen} 
                                onClose={closeImage} 
                                imageUrl={selectedImageUrl} 
                            />
                        }

                        {selectedCompany && (
                            <>
                                <EditBizRegNoModal
                                    isOpen={isBizRegNoModalOpen}
                                    onClose={closeBizRegNoModal}
                                    companyId={selectedCompany.id}
                                    companyName={selectedCompany.name ?? "-"}
                                    bizRegNo={selectedCompany.bizRegNo}
                                />
                                <EditContactModal
                                    isOpen={isContactModalOpen}
                                    onClose={closeContactModal}
                                    companyId={selectedCompany.id}
                                    companyName={selectedCompany.name ?? "-"}
                                    contact={selectedCompany.contact}
                                />
                                <EditHomepageModal
                                    isOpen={isHomepageModalOpen}
                                    onClose={closeHomepageModal}
                                    companyId={selectedCompany.id}
                                    companyName={selectedCompany.name ?? "-"}
                                    homepage={selectedCompany.homepage}
                                />
                                <EditMallModal
                                    isOpen={isMallModalOpen}
                                    onClose={closeMallModal}
                                    companyId={selectedCompany.id}
                                    companyName={selectedCompany.name ?? "-"}
                                    mall={selectedCompany.mall}
                                />
                            </>
                        )}

                        {medicinesCompany && (
                            <MedicinesModal
                                isOpen={isMedicinesModalOpen}
                                onClose={closeMedicinesModal}
                                companyId={medicinesCompany.id}
                                companyName={medicinesCompany.name ?? "-"}
                            />
                        )}

                    </TableContainer>
                ) : (
                    <Spinner color={colors.blue} />
                )}

                {/* pagination */}

                <HStack justifyContent={"center"} marginTop={5} >
                    {/* 이전 페이지 버튼 */}
                    {page > 1 && (
                        <Button size={"xs"} variant={"ghost"} onClick={() => setPage(page - 1)}>Previous</Button>
                    )}
            
                    {/* 첫 페이지와 '...' */}
                    {page > 5 && (
                        <>
                            <Button size={"xs"} variant={"ghost"} onClick={() => setPage(1)}>1</Button>
                            <Text>...</Text>
                        </>
                    )}
            
                    {/* 페이지 버튼들 */}
                    {pages.map((page) => (
                        <Button
                            key={page}
                            size={"xs"} variant={"ghost"}
                            onClick={() => setPage(page)}
                            backgroundColor={page === currentPage ? colors.blue : ""}
                            color={page === currentPage ? colors.white : ""}
                        >
                            {page}
                        </Button>
                    ))}
            
                    {/* 마지막 페이지와 '...' */}
                    {page < totalPages - 4 && (
                        <>
                            <Text>...</Text>
                            <Button size={"xs"} variant={"ghost"} onClick={() => setPage(totalPages)}>{totalPages}</Button>
                        </>
                    )}
            
                    {/* 다음 페이지 버튼 */}
                    {page < totalPages && (
                        <Button size={"xs"} variant={"ghost"} onClick={() => setPage(page + 1)}>Next</Button>
                    )}
                </HStack>
            </Stack>
        </AdminOnly>
    )
}